import * as React from "react";

import { useState } from "react";

import { Button, DropdownItemProps, Divider, Grid, Container, Icon } from "semantic-ui-react";

import moment = require("moment");

import { EntityWeekView } from "./EntityWeekView";
import { CalendarDetails, TimePeriod, Appointment } from "./models";
import { EntitiesDayView } from "./EntitiesDayView";
import { DateSelector } from "./DateSelector";

export type CalendarView = "week" | "day";

export type EntitiesDayViewZoom = "25%" | "50%" | "75%" | "100%";
const defaultZoom = "50%";

export const CalendarMouseEventDebounceMilliseconds = 50;

export interface CalendarProps {
    view: CalendarView;
    details: CalendarDetails[];
    startDate: moment.Moment;
    onEntitySelected?: (entityId: string, entityName: string) => void;
    onViewChange?: (view: CalendarView) => void;
    onFocusDateChange?: (viewStart: moment.Moment) => void;
    className?: string;
    highlight?: TimePeriod;
    children?: any;
    onAppointmentClick?: (appointment: Appointment) => void;
    onAppointmentDrop?: (itemId: string, appointment: Appointment) => void;
    onHourDrop?: (itemId: string, hour: moment.Moment, entityId: string) => void;
    onHourClick?: (hour: moment.Moment, entityId: string) => void;
    showDateRange?: boolean;
    dateOptions?: DropdownItemProps[];
    restrictToAppointmentId?: string;
    showAppointmentCount?: boolean;
    goBack?: () => void;
}

export const Calendar: React.FunctionComponent<CalendarProps> = (props: CalendarProps) => {

    const [focusDate, setFocusDate] = React.useState<moment.Moment>((props.startDate || moment.utc()).clone());
    const [viewAndZoom, setViewAndZoom] = React.useState<{ view: CalendarView; zoom: EntitiesDayViewZoom }>({ view: props.view, zoom: defaultZoom });

    React.useEffect(() => {
        if (props.startDate.diff(focusDate)) {
            setFocusDate(props.startDate);
        }
    }, [props.startDate]);

    const getStartDate = (view: CalendarView) => {
        return focusDate.clone().startOf(view);
    };

    const renderView = () => {
        switch (viewAndZoom.view) {
            case "week":
                return renderWeekView();
            case "day":
                return renderDayView();
            default:
                throw new Error("Calendar view not valid");
        }
    };

    const renderWeekView = () => {
        const startDate = getStartDate("week");
        const endDate = focusDate.clone().endOf("week");
        return (
            <EntityWeekView
                entityId={props.details && props.details[0] && props.details[0].entityId}
                startDate={startDate}
                appointments={props.details && props.details[0] && props.details[0].appointments.filter(appointment =>
                    appointment.start.isSameOrBefore(endDate) && appointment.end.isSameOrAfter(startDate))}
                workingHours={props.details && props.details[0] && props.details[0].workingHours}
                onAppointmentClick={props.onAppointmentClick}
                onAppointmentDrop={props.onAppointmentDrop}
                onHourDrop={props.onHourDrop}
                onHourClick={props.onHourClick}
            />);
    };

    const renderDayView = () => {
        const date = getStartDate("day");
        return (
            <EntitiesDayView
                date={date}
                details={props.details}
                zoom={viewAndZoom.zoom}
                onEntitySelected={props.onEntitySelected}
                onAppointmentClick={props.onAppointmentClick}
                highlight={props.highlight}
                onAppointmentDrop={props.onAppointmentDrop}
                onHourDrop={props.onHourDrop}
                onHourClick={props.onHourClick}
                showAppointmentCount={props.showAppointmentCount}
            />);
    };

    const setDate = (date: moment.Moment) => {
        setFocusDate(date);
        if (props.onFocusDateChange) {
            props.onFocusDateChange(date);
        }
    };

    const onChangeViewAndZoom = (currentView: CalendarView, currentZoom: EntitiesDayViewZoom) => {
        setViewAndZoom({ view: currentView, zoom: currentZoom });
        if (props.onViewChange) {
            props.onViewChange(currentView);
        }
    };

    const [isFilteringHidden, setIsFilteringHidden] = useState(false);

    const toggleHide = () => setIsFilteringHidden(!isFilteringHidden);

    return (
        <Container fluid className={`calendar${props.className ? ` ${props.className}` : ""}`}>
            <Grid container className="calendar-grid">
                {!props.restrictToAppointmentId &&
                    <>
                        <Grid.Row className={isFilteringHidden ? "hide-filters options" : "options"}>
                            <div className="other">
                                {props.children}
                            </div>
                        </Grid.Row>
                        <Grid.Row columns="equal" verticalAlign="middle">
                            <Grid.Column><Divider /></Grid.Column>
                            <Grid.Column textAlign="center" width={3}>
                                <span
                                    className="filters-toggler"
                                    onClick={toggleHide}>
                                    {isFilteringHidden ? "Expand filters" : "Collapse filters"} <Icon name={isFilteringHidden ? "caret down" : "caret up"} />
                                </span>
                            </Grid.Column>
                            <Grid.Column><Divider /></Grid.Column>
                        </Grid.Row>
                    </>
                }
                <Grid.Row divided className="calendar-view-controlers">
                    <DateSelector
                        date={focusDate}
                        onDateChange={setDate}
                        view={props.view}
                        currentDate={moment()}
                        showDateRange={props.showDateRange}
                        dateOptions={props.dateOptions}
                    />
                    <span className="controlers-divider" />
                    <Button.Group basic>
                        <Button
                            className={viewAndZoom.view === "day" ? "view-zoom-active" : ""}
                            content="Daily View"
                            onClick={() => onChangeViewAndZoom("day", defaultZoom)}
                        />
                        <Button
                            className={viewAndZoom.zoom === "100%" ? "view-zoom-active" : ""}
                            content="100%"
                            onClick={() => onChangeViewAndZoom("day", "100%")}
                        />
                        <Button
                            className={viewAndZoom.zoom === "75%" ? "view-zoom-active" : ""}
                            content="75%"
                            onClick={() => onChangeViewAndZoom("day", "75%")}
                        />
                        <Button
                            className={viewAndZoom.zoom === "50%" ? "view-zoom-active" : ""}
                            content="50%"
                            onClick={() => onChangeViewAndZoom("day", "50%")}
                        />
                        <Button
                            className={viewAndZoom.zoom === "25%" ? "view-zoom-active" : ""}
                            content="25%"
                            onClick={() => onChangeViewAndZoom("day", "25%")}
                        />
                    </Button.Group>
                    <Button
                        className={viewAndZoom.view === "week" ? "view-zoom-active" : ""}
                        basic
                        content="Weekly View"
                        onClick={() => onChangeViewAndZoom("week", null)}
                    />
                    {props.restrictToAppointmentId &&
                        <Button secondary
                            content="Back"
                            floated="right"
                            onClick={() => props.goBack()}
                        />
                    }
                </Grid.Row>
                <Grid.Row className="calendar-row">
                    <div className="calendar-view">
                        {renderView()}
                    </div>
                </Grid.Row>
            </Grid>
        </Container>
    );
};
